// src/pages/Terms.js
import React from 'react';

function Terms() {
  return (
    <div style={{ padding: '20px', fontFamily: 'sans-serif', lineHeight: '1.6' }}>
      <h1>TT International Service Terms and Conditions</h1>

      <h2>1. Introduction</h2>
      <p>
        Welcome to TT International. By accessing and using our platform, you agree to abide by
        the following Service Terms and Conditions ("Terms"). These Terms govern your relationship
        with TT International ("TT," "we," "our," "us") regarding the services we provide, including
        ticket advisory, resale management, and investment options. Please read these Terms
        carefully before using the Platform.
      </p>

      <h2>2. Definitions</h2>
      <p>
        • <strong>User:</strong> The individual registered on the Platform, including main account
        holders and Partner accounts.
        <br />
        • <strong>Partner:</strong> A family or friend account linked to the main User for ticket
        purchases.
        <br />
        • <strong>Referral:</strong> An individual introduced to the platform by an existing user,
        who will become a main user.
        <br />
        • <strong>Platform:</strong> TT International’s service for ticket advisory, resale, and
        investment options.
        <br />
        • <strong>Rebate:</strong> Profit share paid after tickets are resold through the platform,
        based on the User’s selected investment option.
        <br />
        • <strong>Investment Options:</strong> Rebate structures offered to users, either 20% or
        40%, based on their upfront investment.
      </p>

      <h2>3. Partner-Accounts and Consent</h2>
      <p>
        • <strong>Partner Accounts:</strong> Users may create Partner accounts for family or
        friends. Each Partner account must have a different residential address to comply with event
        purchase limits and to avoid classification issues. Users must ensure that all Partner
        account holders have given their explicit consent to use the Platform and have agreed to the
        terms outlined in this document. TT will only accept Partner accounts once they have signed
        the consent form sent to them.
        <br />
        • <strong>Real People and Fraud Prevention:</strong> Users must ensure that Partner accounts
        are associated with real individuals and accurate personal details. The use of false or
        fabricated information may constitute fraud or misrepresentation, for which the User will be
        held accountable.
        <br />
        • Users are responsible for ensuring that all Partner account activities, including ticket
        purchases, comply with the event organizer’s rules and platform guidelines.
      </p>

      <h2>4. Referral Program</h2>
      <p>
        • <strong>Referral Rebate:</strong> Users who participate in the referral program are
        entitled to 10% of TT’s profit from the resale of tickets purchased by their referred users.
        <br />
        • <strong>Activity Requirement:</strong> To retain referral status, the main user must
        attend and purchase tickets for at least 50% of scheduled events (with a minimum of 6
        scheduled events per month). If the main user fails to meet this threshold, the 10% referral
        rebate will be removed for one month. However, the rebate will be restored if the user meets
        the threshold the following month.
      </p>

      <h2>5. Ticket Management and Resale</h2>
      <p>
        • All tickets purchased through the Platform must comply with event organizers' ticket
        purchase limits. Users must not use bots, automated software, or false identities to bypass
        these limits.
        <br />
        • <strong>Personal Buys:</strong> If a user intends to personally attend an event, they must
        notify TT before purchasing the tickets. If the user does not notify TT, the tickets will be
        treated as part of the resale process. Should the user later decide to attend the event,
        they will need to purchase tickets at the current market price, which TT can assist with.
        <br />
        • Users agree to forward all relevant ticket details (via email forwarding) to TT for
        resale.
        <br />
        • <strong>Investment Options:</strong>
      </p>
      <ul style={{ listStyle: 'disc', paddingLeft: '40px' }}>
        <li><strong>20% Rebate:</strong> No upfront investment, and users receive 20% of the resale value.</li>
        <li>
          <strong>40% Rebate:</strong> Requires a 50% upfront deposit, and users receive 40% of the resale value.
          The deposit is refunded along with the rebate after the event.
        </li>
      </ul>
      <p>
        • Once tickets are confirmed for resale, ownership is transferred outside the UK for
        management and distribution.
        <br />
        • <strong>Mandatory Use of Resale Services:</strong> Users found using TT’s advisory
        services but not utilizing TT’s resale service risk losing the full value of their
        portfolio.
      </p>

      <h2>6. Fees and Subscription</h2>
      <p>
        • Users must subscribe to TT's platform via the app for a monthly fee of £24.99, after a
        free first month.
        <br />
        • No additional transaction fees apply for ticket resale services.
        <br />
        • <strong>Additional In-App Purchases:</strong> Users can make additional in-app purchases
        for services as needed. These purchases will be listed within the app depending on the
        services required.
      </p>

      <h2>7. Investment and Rebates</h2>
      <p>
        • Rebates will be processed 10 days after the event has taken place.
        <br />
        • <strong>Payment Schedule:</strong> Payment runs for all users will occur every 2 weeks,
        during which the user's "available balance" will be paid out.
        <br />
        • TT reserves the right to modify the rebate structure and notify users at least 30 days in
        advance.
      </p>

      <h2>7A. Compliance Reviews and Payment Holds</h2>
      <p>
        • TT reserves the right to carry out compliance, security and verification checks at any
        time.
        <br />
        • TT may request proof of identity, payment method ownership, source of funds or any other
        information reasonably required.
        <br />
        • TT may temporarily delay or withhold any payment, rebate or withdrawal whilst compliance
        checks or investigations are ongoing.
        <br />
        • Failure to provide requested information may result in delayed payments, account
        suspension or termination.
      </p>

      <h2>7B. Disputed Funds</h2>
      <p>
        • If TT receives evidence of a dispute, or reasonably believes that a dispute exists,
        relating to ticket purchases, payment methods, account ownership, resale proceeds or any
        funds connected with the Platform, TT reserves the right to place the relevant funds on
        hold.
        <br />
        • Funds may remain on hold until TT is reasonably satisfied that the relevant matter has
        been resolved or that the funds can be released.
        <br />
        • TT is not responsible for determining legal ownership of disputed funds, and any payment
        hold does not indicate that TT has taken either party's side.
      </p>

      <h2>8. Data Protection and Privacy</h2>
      <p>
        • TT International complies with all applicable data protection laws, including the General
        Data Protection Regulation (GDPR).
        <br />
        • Personal data is collected and processed solely for ticket resale, management, and rebate
        services.
        <br />
        • Users may request the deletion or modification of their personal data by contacting{' '}
        <a href="mailto:management@ttinternationalltd.co.uk">
          management@ttinternationalltd.co.uk
        </a>
        .
        <br />
        • TT does not share personal data with third parties without explicit consent from the User.
      </p>

      <h2>9. Ticket Details, Sales, and Cancellations</h2>
      <p>
        • <strong>User Responsibility for Ticket Information:</strong> Users are responsible for
        providing accurate and complete information about the tickets, including seat locations,
        event details, and any restrictions, in accordance with the Consumer Rights Act 2015.
        <br />
        • <strong>Finality of Sales:</strong> All ticket sales on the platform are final. Once a
        ticket is sold, the transaction cannot be canceled, refunded, or reversed.
        <br />
        • <strong>Event Changes or Cancellations:</strong> In the event of any changes,
        rescheduling, or cancellation of the event, users are responsible for refunding the original
        value paid for the ticket, including any associated fees.
      </p>

      <h2>10. Security of Tickets and Legal Compliance</h2>
      <p>
        • Once the TT platform has been used and the user has been paid out, the user is legally
        obligated to allow TT to deliver the tickets to the end customer.
        <br />
        • This obligation may include providing TT with Gmail access, primary ticket account access,
        and, where necessary, contacting the event primary (via phone or email).
        <br />
        • In the event that TT does not receive the tickets due to user non-compliance, TT reserves
        the right to take legal action, including recovering loss of earnings and other related
        costs.
        <br />
        • Failure to provide the necessary access or cooperation for ticket delivery may result in
        penalties, including account suspension, fines, or legal action.
      </p>

      <h2>11. Referral Non-Compliance and Penalties</h2>
      <p>
        • <strong>Monetary Penalties:</strong> The platform reserves the right to impose monetary
        penalties for users who fail to fulfill their ticket obligations. These penalties may be
        deducted from any funds held in the referral's portfolio or account.
        <br />
        • <strong>Account Suspension:</strong> Non-compliance may result in the temporary suspension
        of the referral's account on the platform. During this period, the referral will not be able
        to sell tickets.
        <br />
        • <strong>Permanent Account Termination:</strong> Persistent failure to fulfill ticket
        obligations may lead to the permanent termination of the referral’s account on the platform.
        This termination could be accompanied by the removal of any remaining funds or benefits
        associated with the account.
        <br />
        • <strong>Legal Costs Recovery:</strong> In the event of civil action, the referral may be
        held responsible for covering the platform's legal costs incurred during the proceedings.
        <br />
        • <strong>Collection of Compensation:</strong> The platform may seek compensation for any
        damages or losses incurred due to the referral’s non-compliance, in addition to penalties
        and legal costs.
      </p>

      <h2>12. Liability and Indemnity</h2>
      <p>
        • TT International provides ticket resale services as an intermediary. We are not liable for
        penalties, cancellations, or other consequences resulting from a User’s non-compliance with
        event organizer rules, such as exceeding ticket purchase limits, using automated software,
        or providing false details.
        <br />
        • This includes consequences from primary markets, such as tickets being assigned as box
        office collection or primary platforms turning off transfer capabilities, which will be due
        to exceeding limits or other violations.
        <br />
        • Users indemnify TT against any liability resulting from actions taken under their account
        or Partner accounts, including ticket purchases made using bots or false representation.
        <br />
        • TT does not store payment card information; all transactions must be completed using the
        User's own payment methods.
        <br />
        • Users confirm that all payment methods used in connection with the Platform belong to
        them or are used with the permission of the lawful owner. TT reserves the right to request
        evidence of this at any time.
        <br />
        • TT shall not be liable for any delay in releasing funds where such delay is necessary to
        carry out compliance checks or investigate fraud, payment disputes, ticket ownership or
        resale proceeds.
        <br />
        • Users remain solely responsible for any dispute relating to payment methods, funding of
        ticket purchases, ticket ownership or entitlement to resale proceeds.
      </p>

      <h2>13. Dispute Resolution</h2>
      <p>
        • Where a dispute affects funds held by TT, TT reserves the right to retain those funds
        until TT is reasonably satisfied that the relevant matter has been resolved or that the
        funds can be released.
        <br />
        • In the event of a dispute, the parties agree to first seek resolution through mediation.
        <br />
        • If mediation fails, disputes may be resolved through the courts of England and Wales, with
        this Agreement governed by the laws of England and Wales.
      </p>

      <h2>14. Amendments to Terms</h2>
      <p>
        • TT reserves the right to amend these Terms at any time. Users will be notified at least 30
        days in advance of any changes. Continued use of the Platform constitutes acceptance of the
        updated Terms.
      </p>

      <h2>15. Termination</h2>
      <p>
        • TT reserves the right to suspend or terminate a User’s account for violations of these
        Terms, including misuse of the advisory service or non-compliance with event organizer
        rules.
        <br />
        • Users may cancel their subscription at any time. No refunds will be issued for any unused
        portion of the subscription.
      </p>

      <h2>16. Apple-Specific Terms &amp; Conditions</h2>
      <h3>16.1 Relationship with Apple</h3>
      <p>
        By using our iOS application (the “App”), you understand that Apple Inc. (“Apple”) is not a
        party to any contractual relationship between you and TT International regarding our ticket
        advisory, resale, or investment services. Apple’s role is solely that of a third-party
        provider for in-app subscription transactions and billing.
      </p>

      <h3>16.2 Auto-Renewable Subscriptions</h3>
      <p>
        <strong>Auto-Renew:</strong> All subscriptions purchased through the App Store are
        auto-renewable and will renew unless cancelled by you at least 24 hours before the end of
        the current subscription period.
        <br />
        <strong>How to Cancel:</strong> You may cancel your subscription at any time via your Apple
        account settings (e.g., open the App Store → Tap your profile → Subscriptions → Select our
        App).
        <br />
        <strong>Free Trials:</strong> From time to time, we may grant free trial access on a
        case-by-case basis. Free trials may be revoked or ended early if you fail to comply with any
        of these T&amp;Cs or other rules we establish.
      </p>

      <h3>16.3 Refunds for Apple Users</h3>
      <p>
        <strong>Apple Refund Requests:</strong> If you made your purchase through the App Store, you
        must usually request a refund through Apple (via your Apple account or Apple Support).
        Apple’s refund policies and procedures will apply, and TT International has limited control
        over refunds processed directly by Apple.
        <br />
        <strong>Direct Refund Requests:</strong> You may also contact us at{' '}
        <a href="mailto:management@ttinternationalltd.co.uk">
          management@ttinternationalltd.co.uk
        </a>{' '}
        for refund inquiries. We will do our best to assist, but any final decision by Apple is
        outside our control.
      </p>

      <h3>16.4 Apple’s Disclaimer of Liability</h3>
      <p>
        <strong>Apple Not Liable:</strong> Apple has no responsibility or liability for any claims
        related to the purchase, sale, or use of tickets, or for disputes arising from your
        investment or referral activities within the App. All claims or disputes must be resolved
        directly between you (the User) and TT International.
      </p>

      <h3>16.5 Compliance with Apple’s Terms</h3>
      <p>
        You agree to comply with Apple’s Terms of Service (including App Store Terms and Conditions)
        and any applicable usage rules set forth by Apple.
      </p>

      <h3>16.6 Age Restriction &amp; Verification</h3>
      <p>
        Our services and subscriptions are only available to individuals aged 18 or older. By
        downloading or using the App on an iOS device, you represent and warrant that you are at
        least 18 years old.
        <br />
        We verify user identities (including age) during onboarding. Any Apple ID used to access our
        services must belong to an individual 18 years or older.
      </p>

      <h3>16.7 Account Termination on Apple Devices</h3>
      <p>
        If your TT International account is terminated for any violation of these T&amp;Cs, your
        access to the App will be revoked and you will no longer be able to use any subscriber
        features.
        <br />
        <strong>Subscription Refunds:</strong> Whether a subscription refund is granted upon
        termination is handled on a case-by-case basis by TT International. Please contact{' '}
        <a href="mailto:management@ttinternationalltd.co.uk">
          management@ttinternationalltd.co.uk
        </a>{' '}
        for any related inquiries.
      </p>

      <h3>16.8 Notification of Changes &amp; Apple’s Right to Remove</h3>
      <p>
        We may provide updates or changes to these T&amp;Cs by notifying you via email, push
        notifications, or in-app messages.
        <br />
        You acknowledge that Apple reserves the right to remove the App from the App Store or reject
        updates if our T&amp;Cs, subscription flows, or features fail to comply with Apple’s
        guidelines.
      </p>

      <h3>16.9 Customer Support &amp; Disputes</h3>
      <p>
        For subscription-related issues or disputes on iOS devices, you can reach us via dedicated
        Slack channels or by emailing{' '}
        <a href="mailto:management@ttinternationalltd.co.uk">
          management@ttinternationalltd.co.uk
        </a>
        . Our team is available during UK business hours throughout the year.
        <br />
        <strong>In-App Purchases Processed by Apple:</strong> All in-app purchases (including
        subscription payments) are processed through Apple. For payment disputes or billing
        inquiries, you may need to resolve them via your Apple account or Apple’s support system.
      </p>
    </div>
  );
}

export default Terms;
